async function sha256(source: string): Promise<string> {
  const sourceBytes = new TextEncoder().encode(source);
  const digest = await crypto.subtle.digest("SHA-256", sourceBytes);
  const resultBytes = [...new Uint8Array(digest)];
  return resultBytes.map((x) => x.toString(16).padStart(2, "0")).join("");
}

// ------------------------------

export function getWebglRenderer() {
  let canvas = document.createElement("canvas");
  let gl = canvas.getContext("webgl");
  if (!gl) return null;

  let debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  if (!debugInfo) return null;
  return [
    gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
    gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
  ].join(", ");
}

export function getWebglRender() {
  let canvas = document.createElement("canvas");
  canvas.width = 50;
  canvas.height = 50;

  let gl = canvas.getContext("webgl");
  if (!gl) return null;

  let vertices = [
    [-0.1, 0.8, 0.0],
    [-0.8, -0.8, 0.0],
    [0.8, -0.7, 0.0],
  ].flat();
  let vertexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);

  let indices = [0, 1, 2];
  let indexBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
  gl.bufferData(
    gl.ELEMENT_ARRAY_BUFFER,
    new Uint16Array(indices),
    gl.STATIC_DRAW,
  );

  let vertCode = `
      attribute vec3 coordinates;
      void main(void) {
        gl_Position = vec4(coordinates, 1.0);
      }
     `;
  let vertexShader = gl.createShader(gl.VERTEX_SHADER);
  if (!vertexShader) return null;
  gl.shaderSource(vertexShader, vertCode);
  gl.compileShader(vertexShader);

  let fragCode = `
      void main(void) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 0.5);
      }
    `;
  let fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
  if (!fragmentShader) return null;
  gl.shaderSource(fragmentShader, fragCode);
  gl.compileShader(fragmentShader);

  let program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.useProgram(program);

  let coordinatesAttribute = gl.getAttribLocation(program, "coordinates");

  gl.vertexAttribPointer(coordinatesAttribute, 3, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(coordinatesAttribute);

  gl.clearColor(1, 1, 1, 1);
  gl.enable(gl.DEPTH_TEST);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_SHORT, 0);

  return canvas.toDataURL();
}

//---------------------------

export function getDevicePixelRatio() {
  return window.devicePixelRatio;
}

export function getUserAgent() {
  return navigator.userAgent;
}

export function getPlatform() {
  return navigator.platform;
}

export function getPlugins() {
  return Array.from(navigator.plugins)
    .map((plugin) => `${plugin.name} - ${plugin.filename}`)
    .join(", ");
}


export function getDateFormat() {
  return new Date(0).toString();
}

export function getFonts() {
  let fonts = [
    "-apple-system",
    "BlinkMacSystemFont",
    "Cantarell",
    "Consolas",
    "Courier New",
    "Droid Sans",
    "Fira Sans",
    "Helvetica Neue",
    "Menlo",
    "Monaco",
    "Oxygen",
    "Roboto",
    "source-code-pro",
    "Segoe UI",
    "Ubuntu",
  ];
  return fonts
    .filter((font) => document.fonts.check(`12px "${font}"`))
    .join(", ");
}

// ------------------------------
export function getCanvas2dRender() {
  let canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 50;

  let ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.font = "21.5px Arial";
  ctx.fillText("😉", 0, 20);

  ctx.font = "15.7px serif";
  ctx.fillText("abcdefghijklmnopqrtsuvwxyz", 0, 40);

  ctx.font = "20.5px Arial";
  let gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, "red");
  gradient.addColorStop(0.5, "green");
  gradient.addColorStop(1.0, "blue");
  ctx.fillStyle = gradient;
  ctx.fillText("Lorem ipsum!", 30, 20);

  ctx.beginPath();
  ctx.moveTo(170, 5);
  ctx.lineTo(160, 25);
  ctx.lineTo(185, 20);
  ctx.fill();

  return canvas.toDataURL();
}

async function runCatching(f: () => any) {
  try {
    return await f();
  } catch {
    return null;
  }
}

async function computeStableComponents() {
  return {
    devicePixelRatio: await runCatching(getDevicePixelRatio),
    userAgent: await runCatching(getUserAgent),
    platform: await runCatching(getPlatform),
    plugins: await runCatching(getPlugins),
    dateFormat: await runCatching(getDateFormat),
    fonts: await runCatching(getFonts),
    canvas2dRender: await runCatching(getCanvas2dRender),
    webglRenderer: await runCatching(getWebglRenderer),
    webglRender: await runCatching(getWebglRender),
  };
}

async function computeFingerprintComponents() {
  return {
    ...(await computeStableComponents()),
  };
}

async function computeFingerprint(): Promise<string> {
  let components = await computeStableComponents();
  let concatenated = Object.values(components).map(String).join("\n");
  return sha256(concatenated);
}

export { computeFingerprintComponents, computeFingerprint };
